import { api } from './api'

// ─── Fee Management ────────────────────────────────────────────────────────────

export interface FeeCategory {
  id: string
  name: string
  description: string
  school_id: string
  created_at: string
  updated_at: string
}

export interface FeeCategoryCreate {
  name: string
  description: string
  school_id: string
}

export interface FeeStructure {
  id: string
  school_id: string
  category_id: string
  grade_level: string | null
  amount: number
  currency: string
  installments_allowed: number
  due_date: string | null
  late_fee_percent: number
  is_active: boolean
}

export interface FeeStructureCreate {
  category_id: string
  school_year_id?: string
  grade_level?: string
  amount: number
  currency?: string
  installments_allowed?: number
  due_date?: string
  late_fee_percent?: number
}

export interface StudentFeeAssign {
  fee_structure_id: string
  student_id: string
  total_amount: number
  due_date?: string
  notes?: string
}

export interface FeePayment {
  id: string
  student_fee_id: string
  amount: number
  payment_method: string
  reference_number: string
  paid_at: string
}

export interface LiveTrip {
  id: string
  school_id: string
  route_id: string
  vehicle_id: string
  driver_id?: string | null
  direction: string
  status: string
  started_at?: string | null
  ended_at?: string | null
  last_latitude?: number | null
  last_longitude?: number | null
  last_speed_kph?: number | null
  last_heading?: number | null
  last_position_at?: string | null
  is_stale?: boolean
  route_name?: string | null
  plate_number?: string | null
  driver_name?: string | null
}

export interface TripTrackStop {
  route_stop_id: string
  name: string
  stop_order: number
  distance_m: number
  eta_minutes?: number | null
  arrived: boolean
}

export interface TripTrack {
  trip: LiveTrip
  path: Array<{ latitude: number; longitude: number; recorded_at: string; speed_kph?: number | null }>
  stops: TripTrackStop[]
}

export interface ChildBus {
  student_id: string
  student_name?: string | null
  stop_name?: string | null
  trip?: LiveTrip | null
  eta_minutes?: number | null
  distance_m?: number | null
  arrived: boolean
  message?: string | null
}

export interface MoMoAvailability {
  enabled: boolean
  provider: string
  currency: string
  operators: string[]
}

export interface MoMoCollectRequest {
  student_fee_id: string
  amount: number
  phone_number: string
  payer_name?: string
  description?: string
}

export type MoMoCollectionStatus = 'pending' | 'successful' | 'failed' | 'expired' | 'cancelled'

export interface MoMoCollection {
  id: string
  school_id: string
  student_fee_id: string
  amount: number
  currency: string
  phone_number: string
  payer_name?: string | null
  operator?: 'mtn' | 'orange' | null
  provider: string
  provider_reference?: string | null
  external_reference?: string | null
  status: MoMoCollectionStatus
  failure_reason?: string | null
  fee_payment_id?: string | null
  confirmed_at?: string | null
  created_at?: string | null
}

export interface FeePaymentCreate {
  student_fee_id: string
  amount: number
  payment_method: string
  payment_date?: string
  reference_number: string
}

export interface InvoiceItem {
  description: string
  amount: number
}

export interface InvoiceCreate {
  student_id: string
  school_year_id: string
  items: InvoiceItem[]
}

export interface Invoice {
  id: string
  student_id: string
  school_year_id: string
  items: InvoiceItem[]
  total: number
  status: string
  created_at: string
}

export interface StudentFee {
  id: string
  student_id: string
  fee_structure_id: string
  amount_due: number
  amount_paid: number
  status: string
  payments: FeePayment[]
}

export interface FeeSummary {
  total_expected: number
  total_collected: number
  total_outstanding: number
  collection_rate: number
  students_with_arrears: number
}

export interface FeeDiscount {
  id: string
  school_id: string
  student_id: string
  fee_structure_id: string | null
  discount_type: string
  discount_percent: number
  discount_amount: number
  reason: string | null
  is_active: boolean
}

export interface FeeDiscountCreate {
  student_id: string
  fee_structure_id?: string
  discount_type: string
  discount_percent?: number
  discount_amount?: number
  reason?: string
}

export interface SchoolPayment {
  id: string
  student_fee_id: string
  student_id: string
  student_name: string
  class_name: string
  category_name: string
  amount: number
  payment_method: string
  payment_date: string | null
  reference_number: string | null
  receipt_number: string | null
  total_amount: number
  balance: number
}

export interface StudentFeeWithName {
  id: string
  student_id: string
  student_name: string
  class_name?: string
  category_name?: string
  fee_structure_id: string
  total_amount: number
  amount_paid: number
  balance: number
  status: string
  due_date: string | null
  notes: string | null
  payments_count?: number
}

export interface FeeReminderConfig {
  id: string
  school_id: string
  reminder_days_before: number
  overdue_reminder_interval: number
  enable_pre_due_reminders: boolean
  enable_overdue_reminders: boolean
  max_overdue_reminders: number
}

export interface FeeReminderConfigUpdate {
  reminder_days_before?: number
  overdue_reminder_interval?: number
  enable_pre_due_reminders?: boolean
  enable_overdue_reminders?: boolean
  max_overdue_reminders?: number
}

export interface FeeReminderResult {
  pre_due_sent: number
  overdue_sent: number
  errors: number
  details: string[]
}

// ─── Payroll Management ────────────────────────────────────────────────────────

export interface PayrollConfig {
  id: string
  school_id: string
  tax_rate: number
  social_security_rate: number
  pension_rate: number
  pay_day: number
  currency: string
}

export interface PayrollConfigCreate {
  school_id: string
  tax_rate: number
  social_security_rate: number
  pension_rate: number
  pay_day: number
  currency: string
}

export interface TeacherSalary {
  id: string
  teacher_id: string
  school_id: string
  base_salary: number
  transport_allowance: number
  housing_allowance: number
  other_allowances: number
  gross_salary: number
  net_salary: number
}

export interface TeacherSalaryCreate {
  teacher_id: string
  school_id: string
  base_salary: number
  transport_allowance: number
  housing_allowance: number
  other_allowances: number
}

export interface PaySlip {
  id: string
  teacher_id: string
  school_id: string
  month: number
  year: number
  gross_salary: number
  deductions: number
  net_salary: number
  status: string
  paid_at: string | null
  approved_at: string | null
}

export interface PaySlipGenerateRequest {
  school_id: string
  month: number
  year: number
}

export interface PayrollSummary {
  total_payroll: number
  total_deductions: number
  total_net: number
  total_teachers: number
  pending_approvals: number
  paid_count: number
}

// ─── SMS / WhatsApp Notifications ──────────────────────────────────────────────

export interface NotificationTemplate {
  id: string
  school_id: string
  name: string
  content: string
  trigger: string
  channel: string
  created_at: string
}

export interface NotificationTemplateCreate {
  school_id: string
  name: string
  content: string
  trigger: string
  channel: string
}

export type NotificationChannel = 'sms' | 'whatsapp' | 'email'

export interface SendNotificationRequest {
  school_id: string
  recipient_ids: string[]
  message: string
  channel: NotificationChannel
}

export interface SendTemplateRequest {
  template_id: string
  recipient_ids: string[]
  variables: Record<string, string>
}

export interface BroadcastRequest {
  school_id: string
  message: string
  channel: NotificationChannel
  /** 'all' | 'teachers' | 'parents' | 'students' */
  targets: string[]
  /** Only the parents of these pupils; {pupil} in the message is their name */
  student_ids?: string[]
}

export interface NotificationLog {
  id: string
  school_id: string
  recipient_id: string
  message: string
  channel: string
  status: string
  sent_at: string
}

export interface NotificationPreferences {
  user_id: string
  sms_enabled: boolean
  whatsapp_enabled: boolean
  email_enabled: boolean
}

export interface NotificationSummary {
  total_sent: number
  sms_count: number
  whatsapp_count: number
  email_count: number
  delivery_rate: number
}

// ─── Inventory Management ──────────────────────────────────────────────────────

export interface InventoryCategory {
  id: string
  school_id: string
  name: string
  description: string
  asset_count: number
}

export interface InventoryCategoryCreate {
  school_id: string
  name: string
  description: string
}

export interface Asset {
  id: string
  school_id: string
  category_id: string
  name: string
  asset_code: string
  purchase_date: string
  purchase_price: number
  location: string
  condition: string
  status: string
  assigned_to: string | null
  assigned_to_type: string | null
}

export interface AssetCreate {
  school_id: string
  category_id: string
  name: string
  asset_code: string
  purchase_date: string
  purchase_price: number
  location: string
  condition: string
  status: string
}

export interface AssetAssignRequest {
  assigned_to: string
  assigned_to_type: string
  location: string
  notes: string
}

export interface AssetReturnRequest {
  condition: string
  notes: string
}

export interface MaintenanceRecord {
  id: string
  asset_id: string
  maintenance_type: string
  description: string
  cost: number
  performed_by: string
  scheduled_date: string
  completed_date: string | null
}

export interface MaintenanceRecordCreate {
  asset_id: string
  maintenance_type: string
  description: string
  cost: number
  performed_by: string
  scheduled_date: string
}

export interface InventorySummary {
  total_assets: number
  total_value: number
  assigned_count: number
  available_count: number
  maintenance_due: number
  categories_count: number
}

// ─── Transport Management ──────────────────────────────────────────────────────

export interface TransportRoute {
  id: string
  school_id: string
  route_name: string
  start_point: string
  end_point: string
  distance_km: number
  estimated_time_minutes: number
  monthly_fee: number
  stops: RouteStop[]
}

export interface TransportRouteCreate {
  school_id: string
  route_name: string
  start_point: string
  end_point: string
  distance_km: number
  estimated_time_minutes: number
  monthly_fee: number
}

export interface RouteStop {
  id: string
  route_id: string
  stop_name: string
  stop_order: number
  pickup_time: string
  dropoff_time: string
  latitude: number
  longitude: number
}

export interface RouteStopCreate {
  stop_name: string
  stop_order: number
  pickup_time: string
  dropoff_time: string
  latitude: number
  longitude: number
}

export interface Vehicle {
  id: string
  school_id: string
  vehicle_number: string
  vehicle_type: string
  capacity: number
  driver_name: string
  driver_phone: string
  insurance_expiry: string
}

export interface VehicleCreate {
  school_id: string
  vehicle_number: string
  vehicle_type: string
  capacity: number
  driver_name: string
  driver_phone: string
  insurance_expiry: string
}

export interface StudentTransportEnroll {
  /** the pupil's user id */
  student_id: string
  route_id: string
  stop_id?: string
  direction: string
}

export interface TransportSummary {
  total_routes: number
  total_vehicles: number
  total_students_enrolled: number
  total_stops: number
  monthly_revenue: number
}

// ─── Document Management ───────────────────────────────────────────────────────

export interface DocumentCategory {
  id: string
  school_id: string
  name: string
  description: string
  document_type: string
  required: boolean
}

export interface DocumentCategoryCreate {
  school_id: string
  name: string
  description: string
  document_type: string
  required: boolean
}

export interface StudentDocument {
  id: string
  student_id: string
  category_id: string
  school_id: string
  file_name: string
  file_url: string
  file_size: number
  mime_type: string
  academic_year: string
  verified: boolean
  verified_at: string | null
  uploaded_at: string
}

export interface DocumentUploadRequest {
  student_id: string
  category_id: string
  school_id: string
  file_name: string
  file_url: string
  file_size: number
  mime_type: string
  academic_year: string
}

export interface DocumentShareRequest {
  document_id: string
  shared_with_id: string
  access_level: string
  expires_at: string
}

export interface SharedDocument {
  id: string
  document_id: string
  shared_with_id: string
  access_level: string
  expires_at: string
  shared_at: string
  document: StudentDocument
}

export interface DocumentSummary {
  total_documents: number
  verified_count: number
  pending_count: number
  categories_count: number
  total_size_bytes: number
}

// ─── Service Class ─────────────────────────────────────────────────────────────

/** Hand a fetched blob to the browser as a download, then release the URL. */
function saveBlob(data: BlobPart, filename: string): void {
  const blob = new Blob([data], { type: 'application/pdf' })
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
}

class SchoolModulesService {
  // ── Fees ──────────────────────────────────────────────────────────────────

  async getFeeCategories(schoolId?: string): Promise<FeeCategory[]> {
    const params = schoolId ? { school_id: schoolId } : {}
    const response = await api.get<FeeCategory[]>('/fees/categories', { params })
    return response.data
  }

  async createFeeCategory(data: FeeCategoryCreate): Promise<FeeCategory> {
    const response = await api.post<FeeCategory>('/fees/categories', data)
    return response.data
  }

  async getFeeSummary(schoolId: string): Promise<FeeSummary> {
    const response = await api.get<FeeSummary>(`/fees/summary/${schoolId}`)
    return response.data
  }

  async createFeeStructure(data: FeeStructureCreate): Promise<FeeStructure> {
    const response = await api.post<FeeStructure>('/fees/structures', data)
    return response.data
  }

  async assignFeeToStudent(data: StudentFeeAssign): Promise<StudentFee> {
    const response = await api.post<StudentFee>('/fees/assign', data)
    return response.data
  }

  /** A fee for every pupil of a class; those who already have it are skipped. */
  async assignFeeToClass(data: { fee_structure_id: string; classroom_id: string; total_amount: number; due_date?: string }): Promise<{ assigned_count: number; skipped_count: number; total_students: number }> {
    const response = await api.post('/fees/assign-bulk', null, { params: data })
    return response.data
  }

  /** Every payment the school recorded, newest first. */
  async listSchoolPayments(): Promise<SchoolPayment[]> {
    const response = await api.get<SchoolPayment[]>('/fees/payments')
    return response.data
  }

  async recordPayment(data: FeePaymentCreate): Promise<FeePayment> {
    const response = await api.post<FeePayment>('/fees/payments', {
      ...data,
      payment_date: data.payment_date || new Date().toISOString().split('T')[0]
    })
    return response.data
  }

  // ── Mobile money collection ────────────────────────────────────────────
  // Unlike recordPayment (which logs money the school already holds), these
  // pull funds from the payer's MTN MoMo / Orange Money wallet.

  async momoAvailability(): Promise<MoMoAvailability> {
    const response = await api.get<MoMoAvailability>('/fees/momo/status')
    return response.data
  }

  async momoCollect(data: MoMoCollectRequest): Promise<MoMoCollection> {
    const response = await api.post<MoMoCollection>('/fees/momo/collect', data)
    return response.data
  }

  async momoCollectionStatus(id: string): Promise<MoMoCollection> {
    const response = await api.get<MoMoCollection>(`/fees/momo/collections/${id}`)
    return response.data
  }

  async momoCollections(params?: { status?: string; limit?: number }): Promise<MoMoCollection[]> {
    const response = await api.get<MoMoCollection[]>('/fees/momo/collections', { params })
    return response.data
  }

  /** Read back the payslip identity currently stored on a staff user. */
  async getStaffIdentity(userId: string): Promise<{
    cnps_number?: string | null
    job_title?: string | null
    professional_category?: string | null
    employee_id_number?: string | null
  }> {
    const response = await api.get(`/users/${userId}`)
    return response.data
  }

  /** Set the payslip identity fields on a staff user record. */
  async updateStaffIdentity(
    userId: string,
    fields: Partial<{
      cnps_number: string
      job_title: string
      professional_category: string
      employee_id_number: string
      department: string
    }>,
  ): Promise<void> {
    await api.put(`/users/${userId}`, fields)
  }

  // ── Live bus tracking ─────────────────────────────────────────────────
  // Positions come from the drivers' phones, so every read can legitimately
  // return nothing: no trip started, or the handset is offline.

  async getActiveTrips(): Promise<LiveTrip[]> {
    const response = await api.get<LiveTrip[]>('/transport/trips/active')
    return response.data
  }

  async getTripTrack(tripId: string): Promise<TripTrack> {
    const response = await api.get<TripTrack>(`/transport/trips/${tripId}/track`)
    return response.data
  }

  async startBusTrip(data: {
    route_id: string
    vehicle_id: string
    direction?: 'pickup' | 'dropoff' | 'both'
  }): Promise<LiveTrip> {
    const response = await api.post<LiveTrip>('/transport/trips/start', data)
    return response.data
  }

  async endBusTrip(tripId: string): Promise<LiveTrip> {
    const response = await api.post<LiveTrip>(`/transport/trips/${tripId}/end`)
    return response.data
  }

  /** Upload GPS fixes. Batched so the driver's app can queue while offline. */
  async sendBusPositions(
    tripId: string,
    points: Array<{
      latitude: number
      longitude: number
      speed_kph?: number
      heading?: number
      accuracy_m?: number
      recorded_at?: string
    }>,
  ): Promise<{ accepted: number; rejected: number; arrivals: any[] }> {
    const response = await api.post(`/transport/trips/${tripId}/positions`, { points })
    return response.data
  }

  /** Parent view: where each of my children's buses is. */
  async getMyChildrenBus(): Promise<ChildBus[]> {
    const response = await api.get<ChildBus[]>('/transport/my-children-bus')
    return response.data
  }

  // ── Printable documents ───────────────────────────────────────────────
  // Both endpoints require an auth header, so the file is fetched as a blob
  // rather than linked to directly.

  async downloadPaySlipPdf(payslipId: string, filename?: string): Promise<void> {
    const response = await api.get(`/payroll/payslips/${payslipId}/pdf`, {
      responseType: 'blob',
    })
    saveBlob(response.data, filename || `bulletin-de-paie-${payslipId}.pdf`)
  }

  async downloadFeeReceiptPdf(receiptNumber: string): Promise<void> {
    const response = await api.get(
      `/fees/receipt/${encodeURIComponent(receiptNumber)}/pdf`,
      { responseType: 'blob' },
    )
    saveBlob(response.data, `recu-${receiptNumber}.pdf`)
  }

  async generateInvoice(data: InvoiceCreate): Promise<Invoice> {
    const response = await api.post<Invoice>('/fees/invoices', data)
    return response.data
  }

  async getStudentFees(studentId: string): Promise<StudentFee[]> {
    const response = await api.get<StudentFee[]>(`/fees/student/${studentId}`)
    return response.data
  }

  async getFeeStructures(schoolId?: string): Promise<FeeStructure[]> {
    const params = schoolId ? { school_id: schoolId } : {}
    const response = await api.get<FeeStructure[]>('/fees/structures', { params })
    return response.data
  }

  async listAllStudentFees(status?: string): Promise<StudentFeeWithName[]> {
    const params: Record<string, string> = {}
    if (status) params.status = status
    const response = await api.get<StudentFeeWithName[]>('/fees/student-fees', { params })
    return response.data
  }

  async createDiscount(data: FeeDiscountCreate): Promise<FeeDiscount> {
    const response = await api.post<FeeDiscount>('/fees/discounts', data)
    return response.data
  }

  async listDiscounts(studentId?: string): Promise<FeeDiscount[]> {
    const params: Record<string, string> = {}
    if (studentId) params.student_id = studentId
    const response = await api.get<FeeDiscount[]>('/fees/discounts', { params })
    return response.data
  }

  async getParentFees(): Promise<any[]> {
    const response = await api.get('/parent/fees')
    return response.data
  }

  async getReminderConfig(): Promise<FeeReminderConfig> {
    const response = await api.get<FeeReminderConfig>('/fees/reminders/config')
    return response.data
  }

  async updateReminderConfig(data: FeeReminderConfigUpdate): Promise<FeeReminderConfig> {
    const response = await api.put<FeeReminderConfig>('/fees/reminders/config', data)
    return response.data
  }

  async triggerReminders(): Promise<FeeReminderResult> {
    const response = await api.post<FeeReminderResult>('/fees/reminders/send')
    return response.data
  }

  /** A payment a parent made outside the school (Mobile Money, bank…), for the bursary to confirm. */
  async declarePayment(data: { student_fee_id: string; amount: number; payment_method: string; reference_number?: string }) {
    const response = await api.post('/fees/declarations', data)
    return response.data
  }

  async listDeclarations(status: 'pending' | 'all' = 'pending'): Promise<any[]> {
    const response = await api.get('/fees/declarations', { params: { status } })
    return response.data
  }

  async confirmDeclaration(id: string, note?: string) {
    const response = await api.post(`/fees/declarations/${id}/confirm`, { note })
    return response.data
  }

  async rejectDeclaration(id: string, note: string) {
    const response = await api.post(`/fees/declarations/${id}/reject`, { note })
    return response.data
  }

  /** "Relance" on one pupil's line: that family only. */
  async remindFamily(studentFeeId: string): Promise<{ reminded: number; pupil: string; balance: number }> {
    const response = await api.post(`/fees/student-fees/${studentFeeId}/remind`)
    return response.data
  }

  async getReminderStats(): Promise<{ pre_due_reminders_30d: number; overdue_reminders_30d: number; total_30d: number }> {
    const response = await api.get('/fees/reminders/stats')
    return response.data
  }

  // ── Payroll ───────────────────────────────────────────────────────────────

  async getPayrollConfig(schoolId: string): Promise<PayrollConfig> {
    const response = await api.get<PayrollConfig>(`/payroll/config/${schoolId}`)
    return response.data
  }

  async createPayrollConfig(data: PayrollConfigCreate): Promise<PayrollConfig> {
    const response = await api.post<PayrollConfig>('/payroll/config', data)
    return response.data
  }

  async setTeacherSalary(data: TeacherSalaryCreate): Promise<TeacherSalary> {
    const response = await api.post<TeacherSalary>('/payroll/salaries', data)
    return response.data
  }

  async listSalaries(schoolId: string): Promise<TeacherSalary[]> {
    const response = await api.get<TeacherSalary[]>(`/payroll/salaries/${schoolId}`)
    return response.data
  }

  async generatePaySlips(data: PaySlipGenerateRequest): Promise<PaySlip[]> {
    const response = await api.post<PaySlip[]>('/payroll/payslips/generate', data)
    return response.data
  }

  async approvePaySlip(payslipId: string): Promise<PaySlip> {
    const response = await api.post<PaySlip>(`/payroll/payslips/${payslipId}/approve`)
    return response.data
  }

  async markPaySlipPaid(payslipId: string): Promise<PaySlip> {
    const response = await api.post<PaySlip>(`/payroll/payslips/${payslipId}/pay`)
    return response.data
  }

  async listPaySlips(
    schoolId: string,
    params?: { month?: number; year?: number }
  ): Promise<PaySlip[]> {
    const response = await api.get<PaySlip[]>(`/payroll/payslips/${schoolId}`, { params })
    return response.data
  }

  async getPayrollSummary(schoolId: string): Promise<PayrollSummary> {
    const response = await api.get<PayrollSummary>(`/payroll/summary/${schoolId}`)
    return response.data
  }

  // ── SMS / WhatsApp Notifications ──────────────────────────────────────────

  async getNotificationTemplates(schoolId: string): Promise<NotificationTemplate[]> {
    const response = await api.get<NotificationTemplate[]>(
      `/sms-notifications/templates/${schoolId}`
    )
    return response.data
  }

  async createNotificationTemplate(
    data: NotificationTemplateCreate
  ): Promise<NotificationTemplate> {
    const response = await api.post<NotificationTemplate>(
      '/sms-notifications/templates',
      data
    )
    return response.data
  }

  async sendNotification(data: SendNotificationRequest): Promise<{ message: string }> {
    const response = await api.post<{ message: string }>('/sms-notifications/send', data)
    return response.data
  }

  async sendTemplateNotification(
    data: SendTemplateRequest
  ): Promise<{ message: string }> {
    const response = await api.post<{ message: string }>(
      '/sms-notifications/send',
      data
    )
    return response.data
  }

  async broadcastNotification(data: BroadcastRequest): Promise<{ message: string }> {
    const response = await api.post<{ message: string }>(
      '/sms-notifications/broadcast',
      data
    )
    return response.data
  }

  async getNotificationLogs(schoolId: string): Promise<NotificationLog[]> {
    const response = await api.get<NotificationLog[]>(
      `/sms-notifications/logs/${schoolId}`
    )
    return response.data
  }

  async getNotificationPreferences(userId: string): Promise<NotificationPreferences> {
    const response = await api.get<NotificationPreferences>(
      `/sms-notifications/preferences/${userId}`
    )
    return response.data
  }

  async getNotificationSummary(schoolId: string): Promise<NotificationSummary> {
    const response = await api.get<NotificationSummary>(
      `/sms-notifications/summary/${schoolId}`
    )
    return response.data
  }

  // ── Inventory ─────────────────────────────────────────────────────────────

  async getInventoryCategories(schoolId: string): Promise<InventoryCategory[]> {
    const response = await api.get<InventoryCategory[]>(
      `/inventory/categories/${schoolId}`
    )
    return response.data
  }

  async createInventoryCategory(
    data: InventoryCategoryCreate
  ): Promise<InventoryCategory> {
    const response = await api.post<InventoryCategory>('/inventory/categories', data)
    return response.data
  }

  async getAssets(
    schoolId: string,
    params?: { category_id?: string; status?: string }
  ): Promise<Asset[]> {
    const response = await api.get<Asset[]>(`/inventory/assets/${schoolId}`, { params })
    return response.data
  }

  async createAsset(data: AssetCreate): Promise<Asset> {
    const response = await api.post<Asset>('/inventory/assets', data)
    return response.data
  }

  async assignAsset(assetId: string, data: AssetAssignRequest): Promise<Asset> {
    const response = await api.post<Asset>(
      `/inventory/assets/${assetId}/assign`,
      data
    )
    return response.data
  }

  async returnAsset(assetId: string, data: AssetReturnRequest): Promise<Asset> {
    const response = await api.post<Asset>(
      `/inventory/assets/${assetId}/return`,
      data
    )
    return response.data
  }

  async logMaintenance(data: MaintenanceRecordCreate): Promise<MaintenanceRecord> {
    const response = await api.post<MaintenanceRecord>('/inventory/maintenance', data)
    return response.data
  }

  async getMaintenanceRecords(assetId: string): Promise<MaintenanceRecord[]> {
    const response = await api.get<MaintenanceRecord[]>(
      `/inventory/maintenance/${assetId}`
    )
    return response.data
  }

  async getInventorySummary(schoolId: string): Promise<InventorySummary> {
    const response = await api.get<InventorySummary>(`/inventory/summary/${schoolId}`)
    return response.data
  }

  // ── Transport ─────────────────────────────────────────────────────────────

  async getTransportRoutes(schoolId: string): Promise<TransportRoute[]> {
    const response = await api.get<TransportRoute[]>(`/transport/routes/${schoolId}`)
    return response.data
  }

  async createTransportRoute(data: TransportRouteCreate): Promise<TransportRoute> {
    const response = await api.post<TransportRoute>('/transport/routes', data)
    return response.data
  }

  async addRouteStop(routeId: string, data: RouteStopCreate): Promise<RouteStop> {
    const response = await api.post<RouteStop>(
      `/transport/routes/${routeId}/stops`,
      data
    )
    return response.data
  }

  async getVehicles(schoolId: string): Promise<Vehicle[]> {
    const response = await api.get<Vehicle[]>(`/transport/vehicles/${schoolId}`)
    return response.data
  }

  async createVehicle(data: VehicleCreate): Promise<Vehicle> {
    const response = await api.post<Vehicle>('/transport/vehicles', data)
    return response.data
  }

  /** Credit a pupil's canteen account (student = the pupil's students.id). */
  async topUpCafeteriaCredit(studentId: string, amount: number, description?: string) {
    const response = await api.post('/cafeteria/credits/top-up', { student_id: studentId, amount, description })
    return response.data
  }

  async enrollStudentTransport(data: StudentTransportEnroll): Promise<{ message: string }> {
    const response = await api.post<{ message: string }>(
      '/transport/students/enroll',
      data
    )
    return response.data
  }

  async getRouteStudents(routeId: string): Promise<any[]> {
    const response = await api.get<any[]>(`/transport/students/${routeId}`)
    return response.data
  }

  async getTransportSummary(schoolId: string): Promise<TransportSummary> {
    const response = await api.get<TransportSummary>(`/transport/summary/${schoolId}`)
    return response.data
  }

  // ── Documents ─────────────────────────────────────────────────────────────

  async getDocumentCategories(schoolId: string): Promise<DocumentCategory[]> {
    const response = await api.get<DocumentCategory[]>(
      `/documents/categories/${schoolId}`
    )
    return response.data
  }

  async createDocumentCategory(
    data: DocumentCategoryCreate
  ): Promise<DocumentCategory> {
    const response = await api.post<DocumentCategory>('/documents/categories', data)
    return response.data
  }

  async getStudentDocuments(studentId: string): Promise<StudentDocument[]> {
    const response = await api.get<StudentDocument[]>(
      `/documents/student/${studentId}`
    )
    return response.data
  }

  async uploadDocument(data: DocumentUploadRequest): Promise<StudentDocument> {
    const response = await api.post<StudentDocument>('/documents/upload', data)
    return response.data
  }

  async uploadDocumentFile(file: File, opts: { student_id?: string; category_id?: string; document_type?: string; description?: string }): Promise<StudentDocument> {
    const formData = new FormData()
    formData.append('file', file)
    if (opts.student_id) formData.append('student_id', opts.student_id)
    if (opts.category_id) formData.append('category_id', opts.category_id)
    if (opts.document_type) formData.append('document_type', opts.document_type)
    if (opts.description) formData.append('description', opts.description)
    formData.append('title', file.name)
    const response = await api.post<StudentDocument>('/documents/upload-file', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data
  }

  /** Opens a document's file. Uploaded files are served only to signed-in
   *  staff, so they are fetched with the session and opened as a blob. */
  async openDocumentFile(doc: Pick<StudentDocument, 'id' | 'file_url'> & { file_name?: string; title?: string }): Promise<void> {
    const url = doc.file_url || ''
    if (/^https?:\/\//.test(url)) {
      window.open(url, '_blank', 'noopener')
      return
    }
    // a download (not a new tab): browsers block tabs opened after a fetch
    const response = await api.get(`/documents/${doc.id}/file`, { responseType: 'blob' })
    const blobUrl = URL.createObjectURL(response.data as Blob)
    const link = document.createElement('a')
    link.href = blobUrl
    link.download = doc.file_name || doc.title || 'document'
    link.click()
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000)
  }

  async verifyDocument(documentId: string): Promise<StudentDocument> {
    const response = await api.post<StudentDocument>(
      `/documents/${documentId}/verify`
    )
    return response.data
  }

  async deleteDocument(documentId: string): Promise<{ message: string }> {
    const response = await api.delete<{ message: string }>(
      `/documents/${documentId}`
    )
    return response.data
  }

  async shareDocument(data: DocumentShareRequest): Promise<SharedDocument> {
    const response = await api.post<SharedDocument>('/documents/share', data)
    return response.data
  }

  async getSharedDocuments(userId: string): Promise<SharedDocument[]> {
    const response = await api.get<SharedDocument[]>(`/documents/shared/${userId}`)
    return response.data
  }

  async getDocumentSummary(schoolId: string): Promise<DocumentSummary> {
    const response = await api.get<DocumentSummary>(`/documents/summary/${schoolId}`)
    return response.data
  }

  // ── Seed Demo Data ─────────────────────────────────────────────────────────

  async seedAllDemoData(): Promise<Record<string, unknown>> {
    const results: Record<string, unknown> = {}
    const endpoints = ['school-management', 'fees', 'payroll', 'sms-notifications', 'inventory', 'transport', 'documents']
    for (const ep of endpoints) {
      try {
        const response = await api.post(`/${ep}/seed-demo`)
        results[ep] = response.data
      } catch {
        results[ep] = { error: true }
      }
    }
    return results
  }
}

export const schoolModulesService = new SchoolModulesService()
export default schoolModulesService
